/**
 * Générateur — Il utilise la calculatrice pour déterminer une valeur approchée
 * de la racine carrée d'un nombre positif.
 * Attendu ID : 150 | Niveau : 4ème | Domaine : Nombres (desc 11, O15)
 *
 * Source doc prof (NOMBRE ET CALCUL, cluster 3) : Ex.12 « √65 ; √48 ; √18 … au
 * centième », Ex.13 « √163 ; √32 … au dixième », expressions avec radicaux à la
 * calculatrice. Plan validé 2026-07-14, revu 2026-09-22 :
 *
 *   Carte « centieme »   — arrondi de √n au centième (saisie)
 *   Carte « dixieme »    — arrondi de √n au dixième (saisie)
 *   Carte « expression » — arrondi de a√n au centième (saisie)
 *   Carte « meilleure »  — choisir la meilleure valeur approchée (QCM)
 *
 * Arrondi EXACT par racine entière isqrt (aucun flottant) — le gen et le solveur
 * emploient le MÊME algorithme entier, garantissant l'égalité de l'arrondi. La
 * dernière décimale est toujours non nulle (pas d'ambiguïté « 8,1 » vs « 8,10 »).
 *
 * Revue 2026-09-22 : les trois saisies demandent « l'arrondi » et non plus « une
 * valeur approchée » (une troncature est une valeur approchée légitime, et elle
 * diffère de l'arrondi une fois sur deux : l'élève qui tronquait était compté
 * faux) ; « meilleure », les trois distracteurs sont tirés dans une fenêtre
 * large autour de la réponse (avant : R−2, R−1, R+1, la clé était toujours la
 * troisième valeur dans l'ordre croissant) ; radicaux en KaTeX. Aucune carte
 * supprimée.
 *
 * Calculatrice 2026-09-23 : les QUATRE cartes portent contenu.calculatrice.
 * L'intitulé de l'attendu est « il utilise LA CALCULATRICE pour déterminer une
 * valeur approchée », et trois énoncés disent « à la calculatrice » : l'outil
 * était demandé sans être donné. Le flag est lu par Quiz.jsx et
 * ApercuEleveModal (calculatriceAutorisee, utils/questionUtils) et monte la
 * calculatrice collège à l'écran.
 */

import { randomChoice, randInt, shuffle, makeSaisie, makeQcm, racineTex, tex } from '../../../utils';
import { estCarreParfait } from './nombres';

const ATTENDU_ID = 150;

const L_CENTIEME = 'Arrondi d\'une racine au centième';
const L_DIXIEME = 'Arrondi d\'une racine au dixième';
const L_EXPRESSION = 'Arrondi d\'une expression avec radical';
const L_MEILLEURE = 'Reconnaître la meilleure valeur approchée';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Racine entière exacte (floor) et arrondi entier de coef·√n à k décimales.
const isqrt = (m) => { let a = Math.floor(Math.sqrt(m)); while ((a + 1) * (a + 1) <= m) a++; while (a * a > m) a--; return a; };
const arrondiR = (coef, n, k) => {                     // renvoie l'entier R tel que coef√n ≈ R·10^-k
    const m = coef * coef * n * (10 ** (2 * k));
    const a = isqrt(m);
    return (m - a * a >= a + 1) ? a + 1 : a;
};
const fmtApprox = (R, k) => `${Math.floor(R / 10 ** k)},${String(R % 10 ** k).padStart(k, '0')}`;

// Tire (coef, n) tels que √ irrationnelle et dernière décimale ≠ 0.
const tirer = (coef, nMax, k) => {
    for (let i = 0; i < 200; i++) {
        const n = randInt(2, nMax);
        if (estCarreParfait(n)) continue;
        const R = arrondiR(coef, n, k);
        if (R % 10 !== 0) return { n, R };
    }
    return { n: 2, R: arrondiR(coef, 2, k) };
};

// ═════════ Carte « centieme » — √n au centième (saisie) ═════════

const genCentieme = () => {
    const { n, R } = tirer(1, 200, 2);
    const rep = fmtApprox(R, 2);
    const payload = makeSaisie(rep,
        `<p>À la calculatrice, donne l'arrondi de <b>${racineTex(n)}</b> <b>au centième</b>.</p>`,
        opts(L_CENTIEME));
    // Calculatrice AUTORISÉE (revue 2026-09-23) : l'intitulé de l'attendu dit « il utilise la calculatrice », et l'énoncé le répète.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { n, rep };
    return payload;
};

// ═════════ Carte « dixieme » — √n au dixième (saisie) ═════════

const genDixieme = () => {
    const { n, R } = tirer(1, 900, 1);
    const rep = fmtApprox(R, 1);
    const payload = makeSaisie(rep,
        `<p>À la calculatrice, donne l'arrondi de <b>${racineTex(n)}</b> <b>au dixième</b>.</p>`,
        opts(L_DIXIEME));
    // Calculatrice AUTORISÉE (revue 2026-09-23) : l'intitulé de l'attendu dit « il utilise la calculatrice », et l'énoncé le répète.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { n, rep };
    return payload;
};

// ═════════ Carte « expression » — a√n au centième (saisie) ═════════

const genExpression = () => {
    const coef = randInt(2, 6);
    const { n, R } = tirer(coef, 30, 2);
    const rep = fmtApprox(R, 2);
    const payload = makeSaisie(rep,
        `<p>À la calculatrice, donne l'arrondi de <b>${tex(`${coef}\\sqrt{${n}}`)}</b> <b>au centième</b>.</p>`,
        opts(L_EXPRESSION));
    // Calculatrice AUTORISÉE (revue 2026-09-23) : l'intitulé de l'attendu dit « il utilise la calculatrice », et l'énoncé le répète.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { coef, n, rep };
    return payload;
};

// ═════════ Carte « meilleure » — QCM ═════════

const genMeilleure = () => {
    const { n, R } = tirer(1, 400, 1);
    const correct = fmtApprox(R, 1);
    // Fenêtre large [R−5 ; R+5] hors R, valeurs strictement positives : trois
    // dixièmes tirés au hasard, le rang de la clé dans l'ordre croissant varie.
    const cand = [];
    for (let d = -5; d <= 5; d++) if (d !== 0 && R + d > 0) cand.push(R + d);
    const choix = [correct, ...shuffle(cand).slice(0, 3).map(r => fmtApprox(r, 1))];
    const payload = makeQcm(choix, correct,
        `<p><b>Quelle est la meilleure valeur approchée de ${racineTex(n)} au dixième ?</b></p>`,
        opts(L_MEILLEURE));
    // Calculatrice AUTORISÉE (revue 2026-09-23) : départager quatre valeurs approchées suppose de les élever au carré ; l'attendu porte sur l'usage de la calculatrice.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.params = { n, correct };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    centieme_texte: genCentieme,
    dixieme_texte: genDixieme,
    expression_texte: genExpression,
    meilleure_qcm_unique: genMeilleure,
};

const FORMAT_LABELS = { texte: 'Saisie', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'centieme', label: L_CENTIEME, formatsList: buildFormats('centieme') },
    { id: 'dixieme', label: L_DIXIEME, formatsList: buildFormats('dixieme') },
    { id: 'expression', label: L_EXPRESSION, formatsList: buildFormats('expression') },
    { id: 'meilleure', label: L_MEILLEURE, formatsList: buildFormats('meilleure') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
